import { PrimaryButton, Stack, Text } from "@certn/cascadia";
import {
  BasicButton,
  Divider,
  LinkButton,
  Space,
  FieldErrors,
} from "@certn/cascadia-react";
import { useMediaQuery } from "@certn/cascadia-react/hooks/useMediaQuery";
import { breakpoints } from "@certn/cascadia-tokens";
import { slugify } from "@certn/utils";
import { cx } from "@emotion/css";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import { CollapsibleCardContext } from "../layouts/CollapsibleCardLayout";
import {
  CascadiaControlProps,
  withCascadiaControlProps,
} from "../wrappers/withCascadiaControlProps";

export function QuestionControl(props: CascadiaControlProps) {
  const { isSummary } = useContext(CascadiaJsonFormContext);
  const { isCollapsed, setIsCollapsed, setLastQuestionControlValue } =
    useContext(CollapsibleCardContext);
  const isMobile = useMediaQuery(
    `(max-width: ${breakpoints["breakpoint-sm"].maxWidth.toString()}px)`
  );
  const { t } = useTranslation();

  useEffect(() => {
    const eventHandler = (event: CustomEvent) => {
      if (event.detail.id !== props.id && isMobile && !isCollapsed) {
        setIsCollapsed?.(true);
      }
    };

    if (isMobile) {
      document.addEventListener(
        "check-collapse-question",
        eventHandler as EventListener
      );
    }

    return () => {
      if (isMobile) {
        document.removeEventListener(
          "check-collapse-question",
          eventHandler as EventListener
        );
      }
    };
  }, [isCollapsed, isMobile, setIsCollapsed]);

  if (isSummary) {
    return null;
  }

  const options = props.uischema.options;
  return (
    <Stack gap="space-4">
      <Stack
        orientation="vertical"
        gap="space-4"
        className={cx(props.invalid && "has-errors")}
      >
        <Stack orientation="horizontal" gap="space-2">
          <Text style="global/heading-small" color="text-base">
            {options?.question || props.label}
          </Text>

          <Space />
          {setIsCollapsed && props.data && isCollapsed && (
            <>
              <LinkButton
                textColor="text-weak"
                textStyle="form/body-primary-link"
                testID={
                  "edit-" +
                  slugify(props.label.replace("?", "")) +
                  "-question-control"
                }
                linkStyle="monochrome"
                onPress={() => {
                  setIsCollapsed(false);
                }}
              >
                {t("Edit")}
              </LinkButton>
              <Divider isVertical borderLeftColor="border-base" />
            </>
          )}
          {props.data ? (
            <PrimaryButton
              variant="info"
              leftIcon="check_circle"
              size="small"
              testID={isCollapsed ? "primary-button" : "yes-button"}
              onPress={() => {
                props.handleChange(props.path, undefined);
                setLastQuestionControlValue?.(undefined);
                props.setTouched();
                setIsCollapsed?.(true);
                if (isMobile) {
                  document.dispatchEvent(
                    new CustomEvent("check-collapse-question", {
                      detail: { id: props.id },
                    })
                  );
                }
              }}
            >
              {t("Yes")}
            </PrimaryButton>
          ) : (
            <BasicButton
              onPress={() => {
                props.handleChange(props.path, true);
                setLastQuestionControlValue?.(true);
                props.setTouched();
                setIsCollapsed?.(false);
                if (isMobile) {
                  document.dispatchEvent(
                    new CustomEvent("check-collapse-question", {
                      detail: { id: props.id },
                    })
                  );
                }
              }}
              leftIcon="check_circle"
              size="small"
              testID="yes-button"
            >
              {t("Yes")}
            </BasicButton>
          )}

          <BasicButton
            leftIcon="close"
            size="small"
            inverse={props.data === false}
            onPress={() => {
              props.setTouched();
              if (props.data === false) {
                props.setValue(undefined);
                setLastQuestionControlValue?.(undefined);
              }
              if (props.data === undefined || props.data === true) {
                props.setValue(false);
                setLastQuestionControlValue?.(false);
                setIsCollapsed?.(true);
              }
            }}
            testID="no-button"
          >
            {t("No")}
          </BasicButton>
        </Stack>

        {props.touched && <FieldErrors>{props.errors}</FieldErrors>}
      </Stack>

      {!isCollapsed && !options?.hideTopDivider && <Divider />}
    </Stack>
  );
}

QuestionControl.displayName = "QuestionControl";

export const QuestionControlRenderer =
  withCascadiaControlProps(QuestionControl);
